import { Box, Button, Collapse, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Bot, ChevronDown, ChevronUp, RefreshCw, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';

// Context-specific content
function getDefaultContent(context: string): string {
    switch (context) {
        case 'certificates':
            return 'Basierend auf deinen Skills fehlt dir das AWS Solutions Architect Zertifikat für die Cloud-Kompetenz. Mit diesem Zertifikat könntest du deine Projektchancen um 40% erhöhen.';
        case 'skills':
            return 'Deine React-Skills sind stark! Zeit für TypeScript und Node.js um Full-Stack Developer zu werden. Diese Skills sind in 85% der aktuellen Stellenausschreibungen gefragt.';
        case 'missions':
            return 'Deine nächste Mission: Cloud Architecture Mastery. Basierend auf deinem Profil wäre dies der perfekte nächste Schritt für deine Karriere.';
        default:
            return 'Basierend auf deinem Profil habe ich personalisierte Empfehlungen für deine Weiterentwicklung zusammengestellt.';
    }
}

function getContextData(context: string) {
    switch (context) {
        case 'certificates':
            return {
                mockRecommendations: [
                    '2 Zertifikate laufen in 2 Monaten ab',
                    'Welches Zertifikat soll ich zuerst erneuern?',
                    'Zeige mir ablaufende Zertifikate',
                ],
                alternativeTexts: [
                    'Deine AWS-Kenntnisse sind stark! Erwäge das Azure Fundamentals Zertifikat, um deine Cloud-Expertise zu erweitern.',
                    'Mit deinem React-Background wäre das Next.js Zertifikat ein perfekter nächster Schritt für Full-Stack Development.',
                    'Basierend auf deinen Projekten empfehle ich das Docker Certified Associate Zertifikat für bessere DevOps-Skills.',
                ],
            };
        case 'skills':
            return {
                mockRecommendations: [
                    'Welche Skills fehlen mir für Senior Developer?',
                    'Zeige mir Skill-Gaps für Cloud-Kompetenz',
                    'Empfehle mir Skills für meine Rolle',
                ],
                alternativeTexts: [
                    'TypeScript und Node.js würden deine Frontend-Skills perfekt ergänzen. Diese Kombination ist sehr gefragt.',
                    'Deine React-Kenntnisse sind stark! Kubernetes und Docker würden dich zum Full-Stack DevOps Developer machen.',
                    'Mit Python und Machine Learning könntest du in den AI-Bereich wechseln - sehr zukunftsträchtig!',
                ],
            };
        case 'missions':
            return {
                mockRecommendations: [
                    'Welche Mission passt zu meinem Level?',
                    'Zeige mir Missionen für Cloud Skills',
                    'Wie lange dauert die nächste Mission?',
                ],
                alternativeTexts: [
                    "Mission 'Cloud Native Developer' wäre perfekt für dich. 6 Wochen intensive Weiterbildung.",
                    "Die Mission 'AI Integration Specialist' kombiniert deine Skills mit zukunftsweisenden Technologien.",
                    "Mission 'DevOps Master' - bringe deine Deployment-Skills auf das nächste Level!",
                ],
            };
        default:
            return {
                mockRecommendations: [
                    'Was sind meine nächsten Schritte?',
                    'Zeige mir personalisierte Empfehlungen',
                    'Wie kann ich mich weiterentwickeln?',
                ],
                alternativeTexts: [
                    'Basierend auf deinem Profil empfehle ich dir eine Spezialisierung in Cloud-Technologien.',
                    'Deine Skills zeigen Potenzial für eine Führungsrolle. Leadership-Training wäre der nächste Schritt.',
                    'Mit deiner Erfahrung könntest du als Mentor für Junior-Entwickler fungieren.',
                ],
            };
    }
}

interface T4loAssistantInfoBoxProps {
    content?: string;
    ctaText?: string;
    onCtaClick?: () => void;
    context?: 'certificates' | 'skills' | 'missions' | 'general';
}

function T4loAssistantInfoBox({
    content,
    ctaText = 'Lernpfad anzeigen',
    onCtaClick,
    context = 'general',
}: T4loAssistantInfoBoxProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expanded, setExpanded] = useState(false);
    const [displayedText, setDisplayedText] = useState('');
    const [currentContent, setCurrentContent] = useState(content || getDefaultContent(context));
    const [isTyping, setIsTyping] = useState(false);

    const { mockRecommendations, alternativeTexts } = getContextData(context);

    // Typewriter effect
    useEffect(() => {
        if (isTyping) {
            setDisplayedText('');
            let index = 0;
            const timer = setInterval(() => {
                if (index < currentContent.length) {
                    setDisplayedText(currentContent.slice(0, index + 1));
                    index++;
                } else {
                    setIsTyping(false);
                    clearInterval(timer);
                }
            }, 30);
            return () => clearInterval(timer);
        } else {
            setDisplayedText(currentContent);
        }
    }, [currentContent, isTyping]);

    const handleRefresh = (e: React.MouseEvent) => {
        e.stopPropagation();
        const randomText = alternativeTexts[Math.floor(Math.random() * alternativeTexts.length)];
        setCurrentContent(randomText);
        setIsTyping(true);
    };

    // Update content when context changes
    useEffect(() => {
        if (!content) {
            setCurrentContent(getDefaultContent(context));
        }
    }, [context, content]);

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 2,
                background:
                    theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.25)} 0%, ${alpha(theme.palette.secondary.main, 0.3)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                mb: 3,
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: { lg: 3, xs: 2 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    minHeight: expanded ? 'auto' : isMobile ? 70 : 99,
                    ...(!expanded && {
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                    }),
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: isMobile ? 28 : 32,
                            height: isMobile ? 28 : 32,
                            borderRadius: '50%',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                        }}
                    >
                        <Bot size={isMobile ? 14 : 16} />
                    </Box>
                    <Typography variant={isMobile ? 'body2' : 'subtitle2'} sx={{ fontWeight: 600 }}>
                        T4lo Assistant
                    </Typography>
                    <Sparkles size={isMobile ? 12 : 14} color={theme.palette.primary.main} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {expanded && (
                        <IconButton size="small" onClick={handleRefresh}>
                            <RefreshCw size={16} />
                        </IconButton>
                    )}
                    <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </IconButton>
                </Box>
            </Box>

            {/* Content */}
            <Collapse in={expanded}>
                <Box sx={{ px: { lg: 3, xs: 2 }, pb: { lg: 3, xs: 2 } }}>
                    <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
                        {displayedText}
                    </Typography>

                    {/* Mock Recommendations */}
                    <Box sx={{ mb: 2 }}>
                        {mockRecommendations.map((rec, index) => (
                            <Typography
                                key={index}
                                variant="body2"
                                sx={{
                                    fontSize: '0.75rem',
                                    opacity: 0.7,
                                    mb: 0.5,
                                    pl: 1,
                                    borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    '&:hover': {
                                        opacity: 1,
                                        cursor: 'pointer',
                                        borderLeftColor: theme.palette.primary.main,
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {rec}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Collapse>
        </Paper>
    );
}

export default T4loAssistantInfoBox;
